<template>
  <div v-click-outside="() => $emit('close')" class="participant-filter-drawer">
    <div class="participant-filter-body">
      <div class="participant-filter-header">
        <FriedH3 class="title">{{ t('files_filters') }}</FriedH3>
        <FriedIconButton data-external="close-filter-drawer" @click="$emit('close')">
          <FriedIcon :size="IconSize.Small" icon="close" />
        </FriedIconButton>
      </div>
      <div class="participant-filter-content">
        <hr class="separator" />
        <FriedH4 class="title uploaded-by-title">{{ t('frame_uploaded_by') }}</FriedH4>
        <div>
          <FriedSearchInput
            v-model="searchInput"
            data-external="participant-filter-search-input"
            class="search-participant-input"
            :placeholder="t('search')"
          >
            <template #left>
              <FriedIcon icon="search" />
            </template>
          </FriedSearchInput>
        </div>
        <div class="participant-filter-container">
          <ul v-if="participants.length" class="participant-list">
            <li v-for="participant of participantList" :key="participant.id">
              <FriedCheckbox
                :data-external="`filter-participant-checkbox:${participant.id}`"
                :label="participant.fullName || participant.email"
                :model-value="isParticipantSelected(participant.id)"
                :label-size="LabelSize.Small"
                @update:model-value="selectParticipant(participant.id, $event)"
              />
            </li>
          </ul>
          <FriedParagraph v-else>{{ t('no_participants_found') }}</FriedParagraph>
        </div>
      </div>
      <div class="participant-filter-footer">
        <FriedButton
          data-external="participant-filter-reset-button"
          :button-type="ButtonType.Secondary"
          class="filter-button"
          @click="reset"
        >
          {{ t('clear') }}
        </FriedButton>
        <FriedButton
          data-external="participant-filter-apply-button"
          class="filter-button"
          @click="$emit('filter', { participantIds: selectedIds })"
        >
          {{ t('dr_file_apply_filter') }}
        </FriedButton>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from 'vue';
import type { PropType, Ref } from 'vue';
import type { Recipient } from '@getaccept/lib-shared-new/src/types/Recipient';
import { useI18n } from 'vue-i18n';
import { ButtonType, IconSize, LabelSize } from '@getaccept/fried-tofu';
import type { Participant } from '../../types';

export default defineComponent({
  props: {
    selectedParticipantIds: { type: Array as PropType<string[]>, default: () => [] },
    participants: { type: Array as PropType<Recipient[] | Participant[]>, default: () => [] },
  },
  emits: ['filter', 'close'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const selectedIds: Ref<string[]> = ref([...props.selectedParticipantIds]);
    const searchInput = ref('');

    const isParticipantSelected = (participantId: string) =>
      selectedIds.value.includes(participantId);

    const selectParticipant = (participantId: string, select: boolean) => {
      if (select) {
        selectedIds.value.push(participantId);
        return;
      }
      selectedIds.value = selectedIds.value.filter((id: string) => id !== participantId);
    };

    const participantList = computed(() => {
      const searchRegex = new RegExp(`(\\W|^)${searchInput.value}`, 'i');
      return (props.participants as Participant[]).filter(
        participant =>
          (participant.fullName && participant?.fullName.search(searchRegex) !== -1) ||
          participant?.email.search(searchRegex) !== -1
      );
    });

    const reset = () => {
      selectedIds.value = [];
      searchInput.value = '';
      emit('filter', { participantIds: [] });
    };

    return {
      searchInput,
      participantList,
      selectedIds,

      reset,
      isParticipantSelected,
      selectParticipant,
      t,
      LabelSize,
      ButtonType,
      IconSize,
    };
  },
});
</script>
<style lang="scss" scoped>
@import '../../components/scss/dsr-variables';

.participant-filter-drawer {
  position: fixed;
  right: 0;
  top: $new-top-bar-height;
  bottom: 0;
  z-index: 2;
  display: flex;
  box-shadow: var(--shadow-4);
  width: 400px;
  max-width: 100%;

  @media screen and (max-width: $sm) {
    top: 0;
    width: 100%;
  }
}

.participant-filter-body {
  background: var(--white);
  width: 100%;
  display: flex;
  flex-direction: column;

  .participant-filter-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--spacing-100) var(--spacing-150);
  }

  .participant-filter-content {
    padding: 0 var(--spacing-150) var(--spacing-100);
    display: flex;
    flex-direction: column;
  }

  .separator {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--gray-94);
    height: 0;
  }

  .uploaded-by-title {
    padding: var(--spacing-100) 0 var(--spacing-50);
  }

  .search-participant-input {
    margin: var(--spacing-50) 0;
  }

  .participant-filter-footer {
    padding: var(--spacing-100) var(--spacing-150);
    border-top: 1px solid var(--gray-89);
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    gap: var(--spacing-100);

    .filter-button {
      width: 100%;
    }
  }

  .participant-filter-container {
    display: flex;
    padding: var(--spacing-50) 0;
    overflow-y: auto;

    .participant-checkbox {
      padding-top: 0;
      padding-bottom: 0;
      padding-right: var(--spacing-25);
      flex-shrink: 0;
    }

    .participant-list {
      list-style: none;
      padding: 0;
      margin: 0;
      padding-bottom: var(--spacing-50);
      overflow-x: hidden;
      width: 100%;

      .user-list-item {
        margin-top: var(--spacing-50);
      }
    }
  }
}
</style>
